import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import ToggleButton from "react-toggle-button";
import { Container, Col, Row } from "reactstrap";
import {
  updateUnitHolderStatus,
  getAllUnitHolders,
} from "./../../stores/services/unit-holder.service";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory } from "react-router-dom";
import { BsTable } from "react-icons/bs";
import { FaTable, FaChartBar } from "react-icons/fa";
import UnitHolderReport from "./UnitHolderReport";
import Select from "react-select";
import { useSelector } from "react-redux";
import { useCustomStylesDropDown } from "../../assets/css/customStyle";
import { getAllCompanies } from "../../stores/services/company.service";

// ============================================
// TYPES & INTERFACES
// ============================================
interface SearchFilters {
  folioNo: string;
  name: string;
  unitholderType: string;
  cnicNtn: string;
  companyCode: string;
  zakatExempt: boolean;
  taxStatus: boolean;
  reinvest: boolean;
  advanceSearchCategory: string;
  advanceSearchValue: string;
}

interface UnitHolder {
  folio_no: string;
  name: string;
  cnic: string;
  city: string;
  email: string;
  created_at: string;
  status: string;
  [key: string]: any;
}

interface PaginationInfo {
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  pagingCounter: number;
  prevPage: number | null;
  totalDocs: number;
  totalPages: number;
}

interface StatusModalState {
  isOpen: boolean;
  clientStatus: string;
  code: string;
  loading: boolean;
}

// ============================================
// CONSTANTS
// ============================================
const PAGE_SIZE = 10;

const UNITHOLDER_TYPE_OPTIONS = [
  { label: "Unitholder", value: "Unitholder" },
  { label: "Participant", value: "Participant" },
  { label: "Employee", value: "Employee" },
];

const ADVANCE_SEARCH_OPTIONS = [
  { label: "Folio No", value: "folio_no" },
  { label: "Unitholder Type", value: "unitholder_type" },
  { label: "Name", value: "name" },
  { label: "CNIC", value: "cnic" },
  { label: "NTN", value: "ntn" },
  { label: "Age", value: "age" },
  { label: "DOB", value: "dob" },
  { label: "Risk Factor", value: "risk_factor" },
  { label: "Father/Spouse Name", value: "father_spouse_name" },
  { label: "CNIC Expiry", value: "cnic_expiry" },
  { label: "Mobile", value: "mobile" },
  { label: "Phone", value: "phone" },
  { label: "Address", value: "address" },
  { label: "City", value: "city" },
  { label: "Territory", value: "territory" },
  { label: "Account Title", value: "account_title" },
  { label: "Account No", value: "account_no" },
  { label: "Bank Name", value: "bank_name" },
  { label: "Branch Name", value: "branch_name" },
  { label: "Sales Company", value: "sales_company" },
  { label: "Sales Person", value: "sales_person" },
];

// ============================================
// MAIN COMPONENT
// ============================================
const ClientList: React.FC = () => {
  const history = useHistory();
  const distName: any = history.location?.state;
  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);
  const { customStyles } = useCustomStylesDropDown();

  const email = sessionStorage.getItem("email") || "";
  const amcCode = sessionStorage.getItem("amc_code") || "";

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [loading, setLoading] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [data, setData] = useState<UnitHolder[]>([]);
  const [companyOptions, setCompanyOptions] = useState<any[]>([]);
  const [companyName, setCompanyName] = useState<any>(null);
  const [reportView, setReportView] = useState<boolean>(
    distName ? true : false
  );
  const [searchView, setSearchView] = useState<"Normal" | "Advance">("Normal");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(
    null
  );

  // Search filters state
  const [filters, setFilters] = useState<SearchFilters>({
    folioNo: "",
    name: "",
    unitholderType: "",
    cnicNtn: "",
    companyCode: "",
    zakatExempt: false,
    taxStatus: false,
    reinvest: false,
    advanceSearchCategory: "",
    advanceSearchValue: "",
  });

  // Modal state
  const [statusModal, setStatusModal] = useState<StatusModalState>({
    isOpen: false,
    clientStatus: "",
    code: "",
    loading: false,
  });

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    sessionStorage.removeItem("clientObj");
    sessionStorage.removeItem("investorObj");
    sessionStorage.removeItem("backUnitHolderPath");
    fetchAllCompanies();
    handleSearch(1);
  }, []);

  // ============================================
  // COMPANY FETCH LOGIC
  // ============================================
  const fetchAllCompanies = async () => {
    try {
      setLoadingCompany(true);
      const resp = await getAllCompanies({ email, fetch_all: "all" });

      if (resp.data.status === 200) {
        const options = resp.data?.data?.map((data: any) => ({
          ...data,
          value: data.company_code,
          label: `${data.name_of_company} (${data.company_code})`,
        }));
        setCompanyOptions(options);
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompany(false);
    }
  };

  // ============================================
  // SEARCH LOGIC
  // ============================================
  const buildSearchPayload = useCallback(() => {
    const payload: any = {
      email,
      page_number: currentPage,
      page_size: PAGE_SIZE,
    };

    if (searchView === "Normal") {
      // Search value for partial match on folio_no or name
      const searchTerms: string[] = [];

      if (filters.folioNo.trim()) {
        searchTerms.push(filters.folioNo.trim());
      }

      if (filters.name.trim()) {
        searchTerms.push(filters.name.trim());
      }

      // Combine folio and name into single search_value for backend $or query
      if (searchTerms.length > 0) {
        payload.search_value = searchTerms.join(" ");
      }

      // Unitholder Type
      if (filters.unitholderType) {
        payload.type = filters.unitholderType;

        // If Employee type, send no_amc: true
        if (filters.unitholderType === "Employee") {
          payload.no_amc = "true";
        }
      }

      // Company Code
      if (filters.companyCode) {
        payload.company_code = filters.companyCode;
      }

      // CNIC/NTN
      if (filters.cnicNtn.trim()) {
        payload.cnic_ntn = filters.cnicNtn.trim();
      }

      // Boolean filters
      if (filters.zakatExempt) {
        payload.zakat_exempt = true;
      }

      if (filters.taxStatus) {
        payload.tax_status = true;
      }

      if (filters.reinvest) {
        payload.reinvest = true;
      }
    } else {
      // Advanced search
      if (filters.advanceSearchCategory && filters.advanceSearchValue.trim()) {
        payload.search_category = filters.advanceSearchCategory;
        payload.search_value = filters.advanceSearchValue.trim();
      }
    }

    return payload;
  }, [searchView, filters, currentPage, email]);

  const handleSearch = async (page: number = 1, clearSearch = false) => {
    try {
      setLoading(true);
      setCurrentPage(page);

      const searchPayload = buildSearchPayload();

      const response = await getAllUnitHolders(
        email,
        page,
        PAGE_SIZE,
        clearSearch ? {} : searchPayload
      );

      if (response.data.status === 200) {
        setData(response.data.data);
        setPaginationInfo(response.data.page_info);
      } else {
        toast.error(response.data.message);
        setData([]);
        setPaginationInfo(null);
      }
    } catch (error: any) {
      setData([]);
      setPaginationInfo(null);
      const errorMessage = error.response?.data?.message || "Request Failed!";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      folioNo: "",
      name: "",
      unitholderType: "",
      cnicNtn: "",
      companyCode: "",
      zakatExempt: false,
      taxStatus: false,
      reinvest: false,
      advanceSearchCategory: "",
      advanceSearchValue: "",
    });
    setCompanyName(null);
    setCurrentPage(1);
    handleSearch(1, true);
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage < 1 ||
      (paginationInfo && newPage > paginationInfo.totalPages)
    ) {
      return;
    }
    setCurrentPage(newPage);
    handleSearch(newPage);
  };

  const isSearchDisabled = useMemo(() => {
    if (searchView === "Normal") {
      return (
        !filters.folioNo.trim() &&
        !filters.name.trim() &&
        !filters.unitholderType &&
        !filters.cnicNtn.trim() &&
        !filters.companyCode
      );
    } else {
      return (
        !filters.advanceSearchCategory || !filters.advanceSearchValue.trim()
      );
    }
  }, [searchView, filters]);

  // ============================================
  // STATUS UPDATE LOGIC
  // ============================================
  const handleStatusUpdate = async () => {
    try {
      setStatusModal((prev) => ({ ...prev, loading: true }));

      const newStatus =
        statusModal.clientStatus === "active" ? "deactive" : "active";

      const response = await updateUnitHolderStatus(
        email,
        statusModal.code,
        newStatus
      );

      toast.success(response.data.message);
      setStatusModal({
        isOpen: false,
        clientStatus: "",
        code: "",
        loading: false,
      });
      handleSearch(currentPage);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setStatusModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // ============================================
  // RENDER HELPERS
  // ============================================
  const renderPagination = () => {
    if (!paginationInfo || paginationInfo.totalPages <= 1) return null;

    const { totalPages } = paginationInfo;

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <button
          className="btn btn-sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fa fa-chevron-left mr-2"></i>
          Previous
        </button>

        <div className="d-flex gap-2">
          {currentPage > 5 && (
            <>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handlePageChange(1)}
              >
                1
              </button>
              <span className="btn btn-sm btn-outline-secondary">...</span>
            </>
          )}
          {[...Array(Math.min(totalPages, 5))].map((_, i) => {
            const pageNum = currentPage > 5 ? i + (currentPage - 5) : i + 1;
            return (
              pageNum <= totalPages && (
                <button
                  key={i}
                  className={`btn btn-sm ${
                    currentPage === pageNum
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              )
            );
          })}
          {currentPage < totalPages - 5 && (
            <>
              <span className="btn btn-sm btn-outline-secondary">...</span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handlePageChange(totalPages)}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          className="btn btn-sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <i className="fa fa-chevron-right ml-2"></i>
        </button>
      </div>
    );
  };

  const renderData = () => {
    return data.map((items, index) => (
      <tr key={index}>
        <td>{items.folio_no.replace("UNITHOLDER_", "")}</td>
        <td>{items.name}</td>
        <td>{items.cnic}</td>
        <td>{items.city}</td>
        <td>{items.email}</td>
        <td>{moment(items.created_at).format("DD-MM-YYYY")}</td>
        <td className="captilize">{items.status}</td>
        <td style={{ paddingLeft: "3px" }} className="pointer">
          <div className="mr-3">
            <button
              type="button"
              className="btn-icon btn-link like btn btn-primary btn-sm"
              onClick={() => {
                sessionStorage.setItem("clientObj", JSON.stringify(items));
                history.replace("/admin/view-client");
              }}
            >
              <i className="fa fa-eye"></i>
            </button>

            <button
              type="button"
              className="btn-icon btn-link like btn btn-info btn-sm"
              onClick={() => {
                sessionStorage.setItem("clientObj", JSON.stringify(items));
                history.replace(`/admin/edit-client`);
              }}
            >
              <i className="tim-icons icon-pencil"></i>
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  const renderStatusModal = () => {
    if (!statusModal.isOpen) return null;

    return (
      <Modal className="" dialogClassName="modal60w" show={true}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden="true"
            className="close"
            onClick={() =>
              setStatusModal({
                isOpen: false,
                clientStatus: "",
                code: "",
                loading: false,
              })
            }
            data-dismiss="modal"
            type="button"
          >
            <i className="tim-icons icon-simple-remove"></i>
          </button>
          <h6 className="title title-up">Action</h6>
        </div>
        <div className="modal-body">
          <div className="text-center">
            <Row>
              <div className="mx-auto">
                {statusModal.clientStatus === "active" ? (
                  <img src="assets/ban.svg" alt="" width="70" />
                ) : (
                  <img src="assets/check.svg" alt="" width="70" />
                )}
                <h1 className="pb-3">Are You Sure?</h1>
                Are you sure you want to{" "}
                {statusModal.clientStatus === "active"
                  ? "Deactivate"
                  : "Activate"}{" "}
                this Client?
              </div>
            </Row>
            <Row className="mt-3">
              <Col>
                <button
                  className="btn btn-primary"
                  disabled={statusModal.loading}
                  onClick={handleStatusUpdate}
                >
                  {statusModal.loading ? (
                    <>
                      <span
                        className="spinner-border login-txt spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span className="login-txt"> Loading...</span>
                    </>
                  ) : (
                    "Yes"
                  )}
                </button>
              </Col>
              <Col>
                <button
                  className="btn btn-default"
                  onClick={() =>
                    setStatusModal({
                      isOpen: false,
                      clientStatus: "",
                      code: "",
                      loading: false,
                    })
                  }
                >
                  Cancel
                </button>
              </Col>
            </Row>
          </div>
        </div>
      </Modal>
    );
  };

  const borderRadiusStyle = { borderRadius: 2 };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Container fluid>
            <ToastContainer limit={1} />
            <div className="card">
              <div className="card-header">
                <h4 className="card-title d-flex justify-content-between">
                  Client Listing
                  <div className="d-flex align-items-center">
                    <div className="view-switcher-container mr-3">
                      <div
                        className="btn-group btn-group-toggle"
                        data-toggle="buttons"
                      >
                        <label
                          className={`btn btn-outline-primary btn-sm ${
                            !reportView ? "active" : ""
                          }`}
                          onClick={() => setReportView(false)}
                          style={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            minWidth: "110px",
                            transition: "all 0.3s ease",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <input
                            type="radio"
                            name="viewOptions"
                            checked={!reportView}
                            onChange={() => setReportView(false)}
                            style={{ display: "none" }}
                          />
                          <FaTable className="mr-2" />
                          Table View
                        </label>
                        <label
                          className={`btn btn-outline-primary btn-sm ${
                            reportView ? "active" : ""
                          }`}
                          onClick={() => setReportView(true)}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            minWidth: "110px",
                            transition: "all 0.3s ease",
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <input
                            type="radio"
                            name="viewOptions"
                            checked={reportView}
                            onChange={() => setReportView(true)}
                            style={{ display: "none" }}
                          />
                          <FaChartBar className="mr-2" />
                          Report View
                        </label>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm mt-a float-right"
                      onClick={() => history.replace("/admin/setup-clients")}
                    >
                      <i className="fa fa-plus mr-2"></i> Add New
                    </button>
                  </div>
                </h4>
              </div>

              <div className="card-body">
                {!reportView && (
                  <>
                    <div className="view-switcher-container mr-3 mb-2">
                      <div
                        className="btn-group btn-group-toggle"
                        data-toggle="buttons"
                      >
                        <label
                          className={`btn btn-outline-primary btn-sm ${
                            searchView === "Normal" ? "active" : ""
                          }`}
                          onClick={() => setSearchView("Normal")}
                          style={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            minWidth: "110px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <FaChartBar className="mr-2" />
                          Normal Search
                        </label>
                        <label
                          className={`btn btn-outline-primary btn-sm ${
                            searchView === "Advance" ? "active" : ""
                          }`}
                          onClick={() => setSearchView("Advance")}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            minWidth: "110px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <FaTable className="mr-2" />
                          Advance Search
                        </label>
                      </div>
                    </div>

                    <Row className="align-items-center">
                      {searchView === "Normal" ? (
                        <>
                          <Col md="4">
                            <div className="form-group">
                              <label>Folio No</label>
                              <input
                                type="search"
                                placeholder="Enter Folio Number"
                                className="form-control py-1"
                                value={filters.folioNo}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter" && !isSearchDisabled) {
                                    handleSearch(1);
                                  }
                                }}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    folioNo: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="form-group">
                              <label>Name</label>
                              <input
                                type="search"
                                placeholder="Enter Name"
                                className="form-control py-1"
                                value={filters.name}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="form-group">
                              <label>Unitholder Type</label>
                              <Select
                                value={
                                  filters.unitholderType
                                    ? {
                                        value: filters.unitholderType,
                                        label: filters.unitholderType,
                                      }
                                    : null
                                }
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    unitholderType: e?.value || "",
                                  }))
                                }
                                menuPortalTarget={document.body}
                                menuShouldBlockScroll={true}
                                isClearable={true}
                                isSearchable={true}
                                options={UNITHOLDER_TYPE_OPTIONS}
                                styles={customStyles}
                              />
                            </div>
                          </Col>
                          {filters.unitholderType === "Employee" && (
                            <Col md="4">
                              <div className="form-group">
                                <label>Company Name</label>
                                <Select
                                  value={companyName}
                                  onChange={(selectedOption) => {
                                    setCompanyName(selectedOption);
                                    setFilters((prev) => ({
                                      ...prev,
                                      companyCode: selectedOption?.value || "",
                                    }));
                                  }}
                                  menuPortalTarget={document.body}
                                  menuShouldBlockScroll={true}
                                  isClearable={true}
                                  isSearchable={true}
                                  isLoading={loadingCompany}
                                  options={companyOptions}
                                  styles={customStyles}
                                  placeholder="Select Company"
                                />
                              </div>
                            </Col>
                          )}
                          <Col md="4">
                            <div className="form-group">
                              <label>CNIC/NTN</label>
                              <input
                                type="search"
                                placeholder="Enter CNIC/NTN"
                                className="form-control py-1"
                                value={filters.cnicNtn}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    cnicNtn: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </Col>
                          <Col md="2">
                            <div className="form-group mt-4 d-flex">
                              <ToggleButton
                                inactiveLabel=""
                                activeLabel=""
                                value={filters.zakatExempt}
                                thumbStyle={borderRadiusStyle}
                                trackStyle={borderRadiusStyle}
                                onToggle={() =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    zakatExempt: !prev.zakatExempt,
                                  }))
                                }
                              />
                              <label className="ml-2 mt-1">Zakat Exempt</label>
                            </div>
                          </Col>
                          <Col md="2">
                            <div className="form-group mt-4 d-flex">
                              <ToggleButton
                                inactiveLabel=""
                                activeLabel=""
                                value={filters.taxStatus}
                                thumbStyle={borderRadiusStyle}
                                trackStyle={borderRadiusStyle}
                                onToggle={() =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    taxStatus: !prev.taxStatus,
                                  }))
                                }
                              />
                              <label className="ml-2 mt-1">Tax Status</label>
                            </div>
                          </Col>
                          <Col md="2">
                            <div className="form-group mt-4 d-flex">
                              <ToggleButton
                                inactiveLabel=""
                                activeLabel=""
                                value={filters.reinvest}
                                thumbStyle={borderRadiusStyle}
                                trackStyle={borderRadiusStyle}
                                onToggle={() =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    reinvest: !prev.reinvest,
                                  }))
                                }
                              />
                              <label className="ml-2 mt-1">Reinvest</label>
                            </div>
                          </Col>
                        </>
                      ) : (
                        <>
                          <Col md="4">
                            <div className="form-group">
                              <label>Select Category</label>
                              <Select
                                value={
                                  filters.advanceSearchCategory
                                    ? {
                                        value: filters.advanceSearchCategory,
                                        label: ADVANCE_SEARCH_OPTIONS.find(
                                          (opt) =>
                                            opt.value ===
                                            filters.advanceSearchCategory
                                        )?.label,
                                      }
                                    : null
                                }
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    advanceSearchCategory: e?.value || "",
                                  }))
                                }
                                menuPortalTarget={document.body}
                                isClearable={true}
                                isSearchable={true}
                                options={ADVANCE_SEARCH_OPTIONS}
                                styles={customStyles}
                              />
                            </div>
                          </Col>
                          <Col md="4">
                            <div className="form-group">
                              <label>Search Field</label>
                              <input
                                type="search"
                                placeholder="Enter Search Value"
                                className="form-control py-1"
                                value={filters.advanceSearchValue}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    advanceSearchValue: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </Col>
                        </>
                      )}
                    </Row>

                    <Row>
                      <Col className="mt-2" md="12">
                        <button
                          className="btn btn-primary mr-3 py-2 mt-0"
                          disabled={loading || isSearchDisabled}
                          onClick={() => handleSearch(1)}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border login-txt spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              <span className="login-txt"> Searching...</span>
                            </>
                          ) : (
                            <span>
                              <i className="fa fa-search mr-1"></i>Search
                            </span>
                          )}
                        </button>
                        <button
                          className="btn btn-secondary py-2 mt-0"
                          onClick={handleClearFilters}
                          disabled={loading}
                        >
                          <i className="fa fa-times mr-1"></i>Clear Filters
                        </button>
                      </Col>
                    </Row>
                  </>
                )}

                {loading ? (
                  <div className="d-flex justify-content-center my-5">
                    <i
                      className="fa fa-spinner fa-spin fa-3x"
                      aria-hidden="true"
                    ></i>
                  </div>
                ) : data.length > 0 ? (
                  reportView ? (
                    <UnitHolderReport data={data} />
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Folio No</th>
                              <th>Name</th>
                              <th>CNIC</th>
                              <th>City</th>
                              <th>Email</th>
                              <th>Registration Date</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>{renderData()}</tbody>
                        </table>
                      </div>
                      {renderPagination()}
                    </>
                  )
                ) : (
                  <div className="table-empty">
                    <BsTable className="icon" />
                    <p className="text-center empty-text">No record found</p>
                  </div>
                )}
              </div>
            </div>
            {renderStatusModal()}
          </Container>
        </Col>
      </Row>
    </div>
  );
};

export default ClientList;
